import { parseMessageEvent, PostMessageType } from "@reward-plugin/api/v1";
import { APP_BASEPATH } from "../api";

const CLASSNAME_PREFIX = "rewardPlugin-floating-widget";

export class FloatingWidget {
  private $container: HTMLDivElement;
  private $iframe: HTMLIFrameElement;
  private $close: HTMLButtonElement;

  private state: {
    open: boolean;
    token: string;
    displayClose: boolean;
    onClose?: (event: MouseEvent) => void;
  };

  constructor(
    state: Partial<typeof this.state> & Pick<typeof this.state, "token">,
  ) {
    this.state = {
      token: state.token,
      displayClose: true,
      open: false,
    };

    const cntr = (this.$container = document.createElement("div"));
    cntr.id = `${CLASSNAME_PREFIX}-container`;
    cntr.style.display = "none";

    const iframe = (this.$iframe = document.createElement("iframe"));
    iframe.className = `${CLASSNAME_PREFIX}-widget`;
    iframe.src = "about:blank";
    cntr.append(iframe);

    const close = (this.$close = document.createElement("button"));
    close.className = `${CLASSNAME_PREFIX}-close`;
    close.type = "button";
    close.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;
    close.addEventListener("click", (e) => {
      e.preventDefault();
      this.state.onClose?.(e);
    });
    cntr.append(close);

    const style = document.createElement("style");
    style.textContent =
      `#${cntr.id}{font-family:sans-serif;font-size:16px;box-sizing:border-box;position:fixed;top:15%;right:2em;z-index:9999;transition:all 0.3s;border-radius:8px;overflow:hidden;box-shadow:0 0 10px rgba(0,0,0,0.2);width:460px;height:600px;max-height:80vh;background:white}` +
      `#${cntr.id} *,#${cntr.id} *::before,#${cntr.id} *::after{box-sizing:inherit}` +
      `#${cntr.id} .${iframe.className}{border:0;width:100%;height:100%}` +
      `#${cntr.id} .${close.className}{border:0;padding:0;background:transparent;color:inherit;position:absolute;top:0.75em;right:0.75em;width:1.5em;height:1.5em;cursor:pointer;z-index:1000;font-size:1em;transition:opacity 0.2s}` +
      `#${cntr.id} .${close.className}:hover{opacity:0.8}` +
      `#${cntr.id} .${close.className}[disabled]{pointer-events:none;opacity:0.5}`;
    cntr.append(style);

    document.body.append(cntr);

    this.update(state);
  }

  update(state: Partial<typeof this.state>) {
    const prevState = this.state;
    this.state = { ...this.state, ...state };

    if (prevState.open !== this.state.open) {
      if (this.state.open) {
        this.$container.style.display = "";
        this.state.displayClose = true;

        this.$iframe.src = `${APP_BASEPATH}/embed/widget?t=${encodeURIComponent(this.state.token)}`;

        Object.assign(this.$container.style, {
          right: "-70px",
          opacity: "0",
        });

        window.addEventListener("message", this.messageHandler);

        setTimeout(() => {
          document.addEventListener("click", this.outsideClickHandler);

          Object.assign(this.$container.style, {
            right: "",
            opacity: "",
          });
        }, 1);
      } else {
        Object.assign(this.$container.style, {
          right: "-70px",
          opacity: "0",
        });

        document.removeEventListener("click", this.outsideClickHandler);
        window.removeEventListener("message", this.messageHandler);

        setTimeout(() => {
          this.$container.style.display = "none";
        }, 500);
      }
    }

    if (prevState.displayClose !== this.state.displayClose) {
      this.$close.style.opacity = this.state.displayClose ? "" : "0";
      this.$close.disabled = !this.state.displayClose;
    }
  }

  private outsideClickHandler = (e: MouseEvent) => {
    if (this.$container.contains(e.target as Node)) return;

    this.state.onClose?.(e);
  };

  private messageHandler = (e: MessageEvent) => {
    const origin = /^(https?:\/\/[^/]+)/.exec(APP_BASEPATH)?.[1];
    if (e.origin !== origin) return;

    const message = parseMessageEvent(e);
    if (!message) return;

    if (message.type === PostMessageType.DISPLAY_CLOSE_BUTTON) {
      this.update({ displayClose: message.display });
    }
  };

  deregister() {
    this.$container.remove();

    document.removeEventListener("click", this.outsideClickHandler);
    window.removeEventListener("message", this.messageHandler);
  }
}
